import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Swal from "sweetalert2";
import "./MedicalDocumentManager.css";
import { API_BASE } from "../apiConfig";

const ACCEPT = ".doc,.docx,.xls,.xlsx,.pdf,.jpg,.jpeg,.png,.webp";
const ALLOWED = ["doc", "docx", "xls", "xlsx", "pdf", "jpg", "jpeg", "png", "webp"];
const MAX_SIZE = 10 * 1024 * 1024;

function uid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `grp_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function fileExt(name = "") {
  return String(name).split(".").pop().toLowerCase();
}

function isImageName(name = "") {
  return /\.(jpg|jpeg|png|webp)$/i.test(name);
}

function isPdfName(name = "") {
  return /\.pdf$/i.test(name);
}

function validateFiles(files) {
  for (const file of files) {
    const ext = fileExt(file.name);
    if (!ALLOWED.includes(ext)) throw new Error(`${file.name} is not a supported file type.`);
    if (file.size > MAX_SIZE) throw new Error(`${file.name} exceeds 10 MB.`);
    if (file.size < 1) throw new Error(`${file.name} is empty.`);
  }
}

async function apiJson(url, options = {}) {
  const response = await fetch(url, options);
  const text = await response.text();
  if (!text.trim()) throw new Error(`Server returned an empty response. HTTP ${response.status}`);
  let result;
  try { result = JSON.parse(text); }
  catch { throw new Error(`Server returned invalid JSON. HTTP ${response.status}`); }
  if (!response.ok || result.status === "error") {
    throw new Error(result.msg || result.message || `HTTP ${response.status}`);
  }
  return result;
}

export function documentUrl(doc) {
  const value = doc?.document_url || doc?.file_url || doc?.document_path || doc?.file_path || "";
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  return `${API_BASE}${String(value).replace(/^\/+/, "")}`;
}

export async function uploadDocumentGroup({ patientId, title, files, groupId, visitId = 0, appointmentId = "" }) {
  if (!patientId) throw new Error("Patient ID is required before documents can be uploaded.");
  if (!String(title || "").trim()) throw new Error("Document title is required.");
  if (!files?.length) throw new Error("Choose one or more files.");
  validateFiles(files);

  const data = new FormData();
  data.append("patient_id", String(patientId));
  data.append("visit_id", String(visitId || 0));
  data.append("appointment_id", appointmentId ? String(appointmentId) : "");
  data.append("document_title", String(title).trim());
  data.append("group_id", groupId || uid());
  files.forEach(file => data.append("files[]", file));
  return apiJson(`${API_BASE}upload_medical_documents.php`, { method: "POST", body: data });
}

export async function uploadDocumentGroups(patientId, groups, extra = {}) {
  const validGroups = (groups || []).filter(g => g.title?.trim() && g.files?.length);
  const results = [];
  for (const group of validGroups) {
    results.push(await uploadDocumentGroup({
      patientId,
      title: group.title,
      files: group.files,
      groupId: group.groupId || group.id || uid(),
      visitId: extra.visitId || 0,
      appointmentId: extra.appointmentId || ""
    }));
  }
  return results;
}

export function MedicalDocumentDraftEditor({ groups, onChange, disabled = false, heading = "Documents / Reports" }) {
  const addGroup = () => onChange([...(groups || []), { id: uid(), groupId: uid(), title: "", files: [] }]);

  const updateGroup = (id, patch) => {
    onChange((groups || []).map(g => g.id === id ? { ...g, ...patch } : g));
  };

  const removeGroup = (id) => onChange((groups || []).filter(g => g.id !== id));

  const chooseFiles = (group, event) => {
    const incoming = Array.from(event.target.files || []);
    try {
      validateFiles(incoming);
      updateGroup(group.id, { files: [...(group.files || []), ...incoming] });
    } catch (error) {
      event.target.value = "";
      Swal.fire("Invalid File", error.message, "warning");
    }
  };

  const removeDraftFile = (groupId, index) => {
    const group = (groups || []).find(g => g.id === groupId);
    if (!group) return;
    updateGroup(groupId, { files: group.files.filter((_, i) => i !== index) });
  };

  return (
    <section className="mdm-draft-section">
      <div className="mdm-section-head">
        <div><i className="fa fa-folder-open"></i> <strong>{heading}</strong></div>
        <button type="button" className="mdm-add-group" onClick={addGroup} disabled={disabled}>
          <i className="fa fa-plus"></i> Add Report Title
        </button>
      </div>

      {(groups || []).length === 0 && (
        <div className="mdm-empty-draft">Add a report title such as “Scanning Reports for hand” or “Lab Reports”, then select one or more files.</div>
      )}

      {(groups || []).map((group, groupIndex) => (
        <div className="mdm-draft-group" key={group.id}>
          <div className="mdm-title-row">
            <input
              type="text"
              value={group.title}
              onChange={e => updateGroup(group.id, { title: e.target.value })}
              placeholder={`Report title ${groupIndex + 1}`}
              disabled={disabled}
            />
            <label className="mdm-choose-button">
              <i className="fa fa-paperclip"></i> Choose Files
              <input type="file" hidden multiple accept={ACCEPT} onChange={e => chooseFiles(group, e)} disabled={disabled} />
            </label>
            <button type="button" className="mdm-remove-group" onClick={() => removeGroup(group.id)} disabled={disabled} title="Remove this report group">
              <i className="fa fa-trash"></i>
            </button>
          </div>

          {!!group.files?.length && (
            <div className="mdm-draft-grid">
              {group.files.map((file, index) => {
                const image = file.type?.startsWith("image/") || isImageName(file.name);
                const preview = image ? URL.createObjectURL(file) : "";
                return (
                  <div className="mdm-draft-file" key={`${file.name}-${file.lastModified}-${index}`}>
                    <div className="mdm-thumb">
                      {image ? <img src={preview} alt={file.name} onLoad={() => URL.revokeObjectURL(preview)} /> : <i className={isPdfName(file.name) ? "fa fa-file-pdf-o" : "fa fa-file-text-o"}></i>}
                    </div>
                    <div className="mdm-file-name" title={file.name}>{file.name}</div>
                    <button type="button" onClick={() => removeDraftFile(group.id, index)} disabled={disabled} title="Remove before upload"><i className="fa fa-times"></i></button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </section>
  );
}

export default function MedicalDocumentManager({ patientId, visitId = 0, appointmentId = "", heading = "Medical Documents" }) {
  const [documents, setDocuments] = useState([]);
  const [draftGroups, setDraftGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const addFileInput = useRef(null);
  const [addTarget, setAddTarget] = useState(null);

  const loadDocuments = useCallback(async () => {
    if (!patientId) return;
    try {
      setLoading(true);
      const result = await apiJson(`${API_BASE}get_medical_documents.php?patient_id=${encodeURIComponent(patientId)}`);
      setDocuments(Array.isArray(result.data) ? result.data : Array.isArray(result.documents) ? result.documents : []);
    } catch (error) {
      Swal.fire("Unable to Load Documents", error.message, "error");
    } finally { setLoading(false); }
  }, [patientId]);

  useEffect(() => { loadDocuments(); }, [loadDocuments]);

  const grouped = useMemo(() => {
    const map = new Map();
    documents.forEach(doc => {
      const key = doc.group_id || `legacy_${doc.id}`;
      if (!map.has(key)) map.set(key, {
        groupId: key,
        legacy: !doc.group_id,
        title: doc.document_title || doc.title || "Medical Document",
        documents: []
      });
      map.get(key).documents.push(doc);
    });
    return Array.from(map.values());
  }, [documents]);

  const uploadNewGroups = async () => {
    const usable = draftGroups.filter(g => g.title?.trim() && g.files?.length);
    if (!usable.length) {
      Swal.fire("Documents Required", "Enter a report title and choose one or more files.", "warning");
      return;
    }
    try {
      setUploading(true);
      await uploadDocumentGroups(patientId, usable, { visitId, appointmentId });
      setDraftGroups([]);
      await loadDocuments();
      Swal.fire({ icon: "success", title: "Uploaded", text: "Document group(s) uploaded successfully.", timer: 1300, showConfirmButton: false });
    } catch (error) { Swal.fire("Upload Failed", error.message, "error"); }
    finally { setUploading(false); }
  };

  const deleteDocument = async (doc) => {
    const confirm = await Swal.fire({
      icon: "warning", title: "Remove file?", text: doc.original_name || doc.document_name,
      showCancelButton: true, confirmButtonText: "Yes, remove", confirmButtonColor: "#d33"
    });
    if (!confirm.isConfirmed) return;
    try {
      await apiJson(`${API_BASE}delete_medical_document.php`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ document_id: doc.id, patient_id: patientId })
      });
      setDocuments(prev => prev.filter(x => Number(x.id) !== Number(doc.id)));
    } catch (error) { Swal.fire("Delete Failed", error.message, "error"); }
  };

  const renameGroup = async (group) => {
    const answer = await Swal.fire({
      title: "Edit report title", input: "text", inputValue: group.title,
      inputPlaceholder: "Report title", showCancelButton: true,
      inputValidator: value => !value?.trim() ? "Title is required" : undefined
    });
    if (!answer.isConfirmed) return;
    try {
      await apiJson(`${API_BASE}update_medical_document_group.php`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ patient_id: patientId, group_id: group.groupId, document_title: answer.value.trim() })
      });
      await loadDocuments();
    } catch (error) { Swal.fire("Update Failed", error.message, "error"); }
  };

  const requestAddFiles = group => {
    setAddTarget(group);
    setTimeout(() => addFileInput.current?.click(), 0);
  };

  const addFilesToGroup = async event => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!addTarget || !files.length) return;
    try {
      setUploading(true);
      await uploadDocumentGroup({ patientId, title: addTarget.title, files, groupId: addTarget.groupId, visitId, appointmentId });
      await loadDocuments();
    } catch (error) { Swal.fire("Upload Failed", error.message, "error"); }
    finally { setUploading(false); setAddTarget(null); }
  };

  return (
    <section className="mdm-manager">
      <div className="mdm-section-head">
        <div><i className="fa fa-paperclip"></i> <strong>{heading}</strong></div>
        <span className="mdm-count">{documents.length} file(s)</span>
      </div>

      <input ref={addFileInput} type="file" hidden multiple accept={ACCEPT} onChange={addFilesToGroup} />

      {loading ? <div className="mdm-loading"><i className="fa fa-spinner fa-spin"></i> Loading documents...</div> : (
        <div className="mdm-existing-groups">
          {grouped.length === 0 && <div className="mdm-empty">No uploaded documents yet.</div>}
          {grouped.map(group => (
            <div className="mdm-existing-group" key={group.groupId}>
              <div className="mdm-existing-head">
                <div><i className="fa fa-folder"></i> <strong>{group.title}</strong><small>{group.documents.length} file(s)</small></div>
                <div className="mdm-group-actions">
                  {!group.legacy && <button type="button" onClick={() => renameGroup(group)} title="Edit title"><i className="fa fa-pencil"></i> Title</button>}
                  {!group.legacy && <button type="button" onClick={() => requestAddFiles(group)} disabled={uploading} title="Add more files"><i className="fa fa-plus"></i> Files</button>}
                </div>
              </div>
              <div className="mdm-document-grid">
                {group.documents.map(doc => {
                  const name = doc.original_name || doc.document_name || "Document";
                  const url = documentUrl(doc);
                  const image = isImageName(name);
                  return (
                    <div className="mdm-document" key={doc.id}>
                      <a href={url} target="_blank" rel="noreferrer" className="mdm-document-preview" title="Open preview">
                        {image ? <img src={url} alt={name} /> : <div className="mdm-file-icon"><i className={isPdfName(name) ? "fa fa-file-pdf-o" : "fa fa-file-text-o"}></i></div>}
                      </a>
                      <div className="mdm-file-name" title={name}>{name}</div>
                      <div className="mdm-file-actions">
                        <a href={url} target="_blank" rel="noreferrer" title="Preview"><i className="fa fa-eye"></i></a>
                        <button type="button" onClick={() => deleteDocument(doc)} title="Remove"><i className="fa fa-trash"></i></button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <MedicalDocumentDraftEditor groups={draftGroups} onChange={setDraftGroups} disabled={uploading} heading="Add New Report Group" />
      {draftGroups.length > 0 && (
        <div className="mdm-upload-row">
          <button type="button" className="mdm-upload-all" onClick={uploadNewGroups} disabled={uploading}>
            <i className={uploading ? "fa fa-spinner fa-spin" : "fa fa-upload"}></i> {uploading ? "Uploading..." : "Upload All New Reports"}
          </button>
        </div>
      )}
    </section>
  );
}
